/**
 * Template List Row (compact list view)
 * Updated for 6-role permission system — uses granular canDelete/canDuplicate props.
 */

import { memo } from "react";
import {
  Crown,
  Share2,
  Copy,
  Loader2,
  Trash2,
  Star,
} from "lucide-react";
import type { TemplateMeta } from "../../utils/templateApi";
import { prefetchTemplateContent } from "../../utils/contentPrefetch";
import type { UserRecord } from "../../utils/userApi";
import { getAccent, getInitials, getTagColor, formatShortDate, formatRelativeTime, STATUS_CONFIG, PROJECT_TYPE_CONFIG } from "./dashboardConstants";
import { ActionButton } from "./DashboardWidgets";

export const TemplateListRow = memo(function TemplateListRow({
  template,
  actionLoading,
  isAdmin,
  isDeleting,
  canDelete,
  canDuplicate,
  onOpen,
  onDuplicate,
  onDelete,
  onCancelDelete,
  onShare,
  isFavorite,
  onFavorite,
  allUsers,
}: {
  template: TemplateMeta;
  actionLoading: string | null;
  isAdmin: boolean;
  isDeleting: boolean;
  /** Granular permission: can this user delete templates? (admin + characterizer) */
  canDelete?: boolean;
  /** Granular permission: can this user duplicate templates? (admin + characterizer) */
  canDuplicate?: boolean;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onCancelDelete: () => void;
  onShare: () => void;
  isFavorite?: boolean;
  onFavorite?: () => void;
  /** All users for resolving display names */
  allUsers?: UserRecord[];
}) {
  const accent = getAccent(template.id);
  const isStore = (template.templateType || "branding") === "store";
  const users = allUsers || [];

  const resolveDisplayName = (username: string) => {
    if (!username) return "";
    const u = users.find((usr) => usr.username === username);
    return u ? u.displayName : username;
  };

  const team = [
    resolveDisplayName(template.characterizer),
    resolveDisplayName(template.marketer),
    resolveDisplayName(template.designer),
    resolveDisplayName(template.developer),
    resolveDisplayName(template.contentWriter || ""),
  ]
    .filter(Boolean)
    .join(" \u00B7 ");

  // Use granular permissions, falling back to isAdmin for backward compat
  const effectiveCanDelete = canDelete ?? isAdmin;
  const effectiveCanDuplicate = canDuplicate ?? isAdmin;

  return (
    <div className="grid grid-cols-[minmax(160px,1fr)_70px_60px_minmax(80px,1fr)_80px_minmax(60px,100px)_70px_70px_auto] items-center gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-all group cursor-pointer min-w-[860px]" onClick={onOpen} onMouseEnter={() => prefetchTemplateContent(template.id)}>
      {/* Client name + initials */}
      <div className="flex items-center gap-2.5 min-w-0">
        <div className={`w-8 h-8 bg-gradient-to-br ${accent.bg} rounded-lg flex items-center justify-center shrink-0`}>
          <span className="text-white text-[11px]" style={{ fontWeight: 700 }}>
            {getInitials(template.clientName)}
          </span>
        </div>
        <div className="min-w-0">
          <span className="text-sm text-gray-800 truncate block" style={{ fontWeight: 600 }}>
            {template.clientName || "\u2014"}
          </span>
          {template.clientNumber && (
            <span className="text-[10px] text-gray-400">{template.clientNumber}</span>
          )}
        </div>
      </div>

      {/* Type badge */}
      <div className="flex items-center gap-1 flex-wrap">
        <span className={`text-[10px] px-1.5 py-0.5 rounded border w-fit ${
          isStore
            ? "bg-emerald-50 text-emerald-600 border-emerald-200"
            : "bg-zap-pale text-zap border-zap-light/30"
        }`} style={{ fontWeight: 600 }}>
          {isStore ? "\u05E1\u05D7\u05E8" : "\u05EA\u05D3\u05DE\u05D9\u05EA"}
        </span>
        {template.projectType && PROJECT_TYPE_CONFIG[template.projectType] && (() => {
          const ptCfg = PROJECT_TYPE_CONFIG[template.projectType!];
          return (
            <span
              className="text-[10px] px-1 py-0.5 rounded border"
              style={{ fontWeight: 700, backgroundColor: ptCfg.bg, color: ptCfg.text, borderColor: ptCfg.border }}
            >
              {ptCfg.label}
            </span>
          );
        })()}
        {template.masterSourceId && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200 flex items-center gap-0.5" style={{ fontWeight: 600 }}>
            <Crown className="w-2.5 h-2.5" />
          </span>
        )}
      </div>

      {/* Status badge */}
      <div>
        {template.status && STATUS_CONFIG[template.status] ? (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-1 w-fit"
            style={{
              fontWeight: 600,
              backgroundColor: STATUS_CONFIG[template.status].bg,
              color: STATUS_CONFIG[template.status].text,
              borderColor: STATUS_CONFIG[template.status].border,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_CONFIG[template.status].dot }} />
            {STATUS_CONFIG[template.status].label}
          </span>
        ) : (
          <span className="text-[10px] text-gray-300">{"\u2014"}</span>
        )}
      </div>

      {/* Team */}
      <span className="text-xs text-gray-400 truncate">{team || "\u2014"}</span>

      {/* Current owner */}
      <span className="text-xs text-purple-500 truncate" style={{ fontWeight: template.currentOwner ? 600 : 400 }}>
        {template.currentOwner || "\u2014"}
      </span>

      {/* Tags */}
      <div className="flex flex-wrap gap-0.5 min-w-0">
        {(template.tags || []).length > 0 ? (
          (template.tags || []).slice(0, 3).map((tag) => {
            const tc = getTagColor(tag);
            return (
              <span
                key={tag}
                className="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] border whitespace-nowrap"
                style={{ fontWeight: 500, backgroundColor: tc.bg, color: tc.text, borderColor: tc.border }}
              >
                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: tc.dot }} />
                {tag}
              </span>
            );
          })
        ) : (
          <span className="text-[10px] text-gray-300">{"\u2014"}</span>
        )}
        {(template.tags || []).length > 3 && (
          <span className="text-[9px] text-gray-400">+{(template.tags || []).length - 3}</span>
        )}
      </div>

      {/* Created */}
      <span className="text-xs text-gray-400">{formatShortDate(template.createdAt)}</span>

      {/* Updated */}
      <span className="text-xs text-gray-500" style={{ fontWeight: 500 }}>
        {formatRelativeTime(template.updatedAt)}
        {template.lastEditedBy && (
          <span className="text-gray-400 mr-1" style={{ fontWeight: 400 }}>
            {" \u05E2\u05F4\u05D9 "}{template.lastEditedBy}
          </span>
        )}
      </span>

      {/* Actions */}
      <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
        <ActionButton
          onClick={(e) => { e.stopPropagation(); onShare(); }}
          title={"\u05E9\u05D9\u05EA\u05D5\u05E3"}
          hoverClass="hover:text-emerald-600 hover:bg-emerald-50"
        >
          <Share2 className="w-3.5 h-3.5" />
        </ActionButton>
        {effectiveCanDuplicate && (
          <ActionButton
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            disabled={actionLoading === `dup-${template.id}`}
            title={"\u05E9\u05DB\u05E4\u05DC"}
            hoverClass="hover:text-amber-600 hover:bg-amber-50"
          >
            {actionLoading === `dup-${template.id}` ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </ActionButton>
        )}
        {effectiveCanDelete && (
          isDeleting ? (
            <div className="flex items-center gap-1 mr-1">
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onDelete(); }}
                className="px-2 py-0.5 text-[10px] bg-red-500 text-white rounded hover:bg-red-600 transition-colors cursor-pointer"
                style={{ fontWeight: 600 }}
              >
                {actionLoading === `del-${template.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "\u05DE\u05D7\u05E7"}
              </span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onCancelDelete(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onCancelDelete(); }}
                className="px-2 py-0.5 text-[10px] bg-gray-200 text-gray-600 rounded hover:bg-gray-300 transition-colors cursor-pointer"
              >
                {"\u05D1\u05D9\u05D8\u05D5\u05DC"}
              </span>
            </div>
          ) : (
            <ActionButton
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              title={"\u05DE\u05D7\u05E7"}
              hoverClass="hover:text-red-500 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </ActionButton>
          )
        )}
        {onFavorite && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onFavorite(); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onFavorite(); } }}
            className={`p-1.5 rounded-lg transition-all cursor-pointer ${
              isFavorite
                ? "text-amber-400 hover:text-amber-500"
                : "text-gray-300 hover:text-amber-400"
            }`}
            title={isFavorite ? "\u05D4\u05E1\u05E8 \u05DE\u05DE\u05D5\u05E2\u05D3\u05E4\u05D9\u05DD" : "\u05D4\u05D5\u05E1\u05E3 \u05DC\u05DE\u05D5\u05E2\u05D3\u05E4\u05D9\u05DD"}
          >
            <Star className="w-3.5 h-3.5" fill={isFavorite ? "currentColor" : "none"} />
          </span>
        )}
      </div>
    </div>
  );
});